import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import axios from "axios";
import Header1 from "../components/default-header";
import DefaultFooter from "../components/default-footer";
import { API_URL, Back_url } from "../config";
import { useSeo } from "../components/useSeo";
import { breadcrumbJsonLd } from "../components/seoData";
import Header from "../components/header";

interface CarPhoto {
  id: number;
  path: string;
  is_main: boolean;
  url?: string;
}

interface Car {
  id: number;
  name: string;
  model: string;
  type: string;
  number: string;
  engine_capacity: string;
  color: string;
  price_per_day: string;
  price_per_month: string;
  price_per_year: string;
  photos: CarPhoto[];
}

type RateView = "day" | "month" | "year";

const SeatIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 17v2a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-2" />
    <path d="M5 17V9a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v8" />
    <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);

const GearIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const CarPlaceholder = () => (
  <svg width="64" height="64" viewBox="0 0 200 200" fill="none" aria-hidden="true">
    <path
      d="M45 115 L60 85 C65 75 75 70 90 70 L125 70 C140 70 150 78 158 88 L168 105 C173 107 175 111 175 116 V125 C175 128 172 130 168 130 H162 C162 122 154 115 145 115 C136 115 128 122 128 130 H72 C72 122 64 115 55 115 C46 115 38 122 38 130 H32 C28 130 25 128 25 125 V118 C25 115 28 112 32 112 Z"
      fill="var(--teal-d)" fillOpacity="0.06" stroke="var(--gold)" strokeWidth="1.5"
    />
    <circle cx="55" cy="130" r="9" stroke="var(--gold)" strokeWidth="2" fill="var(--cream)" />
    <circle cx="145" cy="130" r="9" stroke="var(--gold)" strokeWidth="2" fill="var(--cream)" />
  </svg>
);

const CarRental = () => {
  const { t } = useTranslation();
  const [cars, setCars] = useState<Car[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [rateView, setRateView] = useState<RateView>("day");

  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  useSeo({
    title: t("carRentalSeoTitle"),
    description: t("carRentalSeoDescription"),
    canonical: `${origin}/car-rent`,
    jsonLd: breadcrumbJsonLd([
      { name: t("home"), url: `${origin}/` },
      { name: t("carRental"), url: `${origin}/car-rent` },
    ]),
  });

  const fetchCars = async (page = 1) => {
    try {
      setLoading(true);
      setError(false);
      const params: Record<string, string | number> = { page, per_page: 9 };
      if (typeFilter !== "all") params.type = typeFilter;

      const response = await axios.get(`${API_URL}/cars-rental`, { params });
      setCars(response.data.data);
      setCurrentPage(response.data.current_page);
      setLastPage(response.data.last_page);
    } catch (err) {
      console.error("Error fetching cars:", err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCars(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter]);

  const rateLabel = { day: t("daily"), month: t("monthly"), year: t("yearly") }[rateView];
  const priceFor = (car: Car) =>
    rateView === "day" ? car.price_per_day : rateView === "month" ? car.price_per_month : car.price_per_year;

  const [showPromoModal, setShowPromoModal] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowPromoModal(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const closePromoModal = () => setShowPromoModal(false);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');

        .cr-modal-overlay {
          position: fixed; inset: 0; background: rgba(26,49,64,.55);
          backdrop-filter: blur(3px);
          display: flex; align-items: center; justify-content: center;
          z-index: 1000; padding: 20px;
          animation: cr-fade-in .25s ease;
        }
        @keyframes cr-fade-in { from { opacity: 0; } to { opacity: 1; } }

        .cr-modal {
          background: var(--cream); border-radius: 16px; max-width: 440px; width: 100%;
          position: relative; overflow: hidden;
          box-shadow: 0 24px 60px rgba(26,49,64,.35);
          border: 1px solid rgba(211,190,118,.3);
          animation: cr-modal-in .3s cubic-bezier(.2,.9,.3,1.2);
        }
        @keyframes cr-modal-in { from { opacity: 0; transform: translateY(16px) scale(.97); } to { opacity: 1; transform: translateY(0) scale(1); } }

        .cr-modal-banner {
          background: linear-gradient(135deg, var(--teal-d), var(--teal));
          padding: 26px 28px 22px; position: relative;
        }
        .cr-modal-banner::after {
          content: ''; position: absolute; inset: 0;
          background: radial-gradient(60% 80% at 90% 0%, rgba(211,190,118,.25), transparent 70%);
        }
        .cr-modal-tag {
          display: inline-flex; align-items: center; gap: 6px;
          font-size: 10.5px; font-weight: 700; letter-spacing: 1.2px; text-transform: uppercase;
          color: var(--teal-d); background: var(--gold);
          border-radius: 30px; padding: 5px 12px; position: relative; z-index: 1;
        }
        .cr-modal-tag::before { content: '●'; font-size: 8px; color: var(--teal-d); }
        .cr-modal-heading {
          color: #fff; font-size: 22px; font-weight: 600; margin: 14px 0 0; position: relative; z-index: 1;
        }
        .cr-modal-heading em { color: var(--gold-l); font-style: italic; font-weight: 500; }

        .cr-modal-close {
          position: absolute; top: 14px; right: 14px; z-index: 2;
          width: 28px; height: 28px; border-radius: 50%; border: none; cursor: pointer;
          background: rgba(255,255,255,.15); color: #fff; font-size: 15px;
          display: flex; align-items: center; justify-content: center;
          transition: background .2s;
        }
        .cr-modal-close:hover { background: rgba(255,255,255,.28); }

        .cr-modal-body { padding: 22px 28px 28px; }
        .cr-modal-text { font-size: 13.5px; line-height: 1.6; color: var(--teal-l); margin: 0 0 18px; }

        .cr-modal-perks { display: flex; flex-direction: column; gap: 10px; margin-bottom: 22px; }
        .cr-modal-perk { display: flex; align-items: center; gap: 10px; font-size: 12.5px; color: var(--ink); font-weight: 500; }
        .cr-modal-perk-icon {
          width: 22px; height: 22px; border-radius: 50%; flex-shrink: 0;
          background: var(--gold-l); color: var(--gold-d);
          display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700;
        }

        .cr-modal-actions { display: flex; gap: 10px; }
        .cr-modal-cta {
          flex: 1; text-align: center; text-decoration: none; cursor: pointer;
          background: var(--teal); color: #fff; font-size: 13.5px; font-weight: 600;
          border: none; border-radius: 8px; padding: 12px; transition: background .2s;
        }
        .cr-modal-cta:hover { background: var(--teal-d); }
        .cr-modal-dismiss {
          text-align: center; cursor: pointer;
          background: transparent; color: var(--silver); font-size: 13px; font-weight: 500;
          border: 1px solid rgba(41,75,90,.14); border-radius: 8px; padding: 12px 16px;
          transition: border-color .2s, color .2s;
        }
        .cr-modal-dismiss:hover { border-color: var(--teal); color: var(--teal); }

        @media (max-width: 480px) {
          .cr-modal-actions { flex-direction: column; }
        }

        :root {
          --teal: #294b5a; --teal-d: #1a3140; --teal-l: #3b6272;
          --gold: #d3be76; --gold-l: #ede4b5; --gold-d: #b09a50;
          --cream: #f7f4ee; --mist: #edeae2; --silver: #8a9aa3; --ink: #1a2f38;
        }

        .cr-page { font-family: 'DM Sans', sans-serif; background: var(--cream); }
        .cr-page h1, .cr-page h2, .cr-page h3 { font-family: 'Playfair Display', serif; }

        .cr-hero { padding: 150px 24px 50px; text-align: center; position: relative; overflow: hidden; }
        .cr-hero::before {
          content: ''; position: absolute; inset: 0;
          background: radial-gradient(55% 55% at 50% 20%, rgba(211,190,118,.10), transparent 70%);
          pointer-events: none;
        }
        .cr-eyebrow {
          display: inline-flex; align-items: center; gap: 8px;
          font-size: 11px; font-weight: 700; letter-spacing: 1.5px; color: var(--gold-d);
          background: rgba(211,190,118,.12); border: 1px solid rgba(211,190,118,.35);
          border-radius: 30px; padding: 7px 16px; margin-bottom: 18px;
        }
        .cr-eyebrow::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: var(--gold-d); }
        .cr-title { font-size: clamp(1.8rem, 3.2vw, 2.5rem); font-weight: 600; color: var(--ink); margin: 0 0 12px; }
        .cr-title em { color: var(--gold-d); font-style: italic; font-weight: 500; }
        .cr-subtitle { font-size: 15px; color: var(--silver); font-weight: 300; max-width: 560px; margin: 0 auto; }

        .cr-toolbar {
          max-width: 1140px; margin: 0 auto 30px; padding: 0 24px;
          display: flex; flex-wrap: wrap; gap: 14px; align-items: center; justify-content: space-between;
        }
        .cr-filters { display: flex; gap: 8px; flex-wrap: wrap; }
        .cr-pill-btn {
          font-size: 12.5px; font-weight: 600; color: var(--teal);
          background: #fff; border: 1px solid rgba(41,75,90,.14);
          border-radius: 30px; padding: 9px 18px; cursor: pointer; transition: all .2s;
        }
        .cr-pill-btn.active { background: var(--teal); color: #fff; border-color: var(--teal); }
        .cr-pill-btn:hover:not(.active) { border-color: var(--gold-d); color: var(--gold-d); }

        .cr-rate-toggle { display: flex; background: #fff; border: 1px solid rgba(41,75,90,.14); border-radius: 30px; padding: 3px; }
        .cr-rate-btn {
          font-size: 12px; font-weight: 600; color: var(--silver); background: transparent; border: none;
          border-radius: 20px; padding: 7px 14px; cursor: pointer; transition: all .2s;
        }
        .cr-rate-btn.active { background: var(--gold); color: var(--teal-d); }

        .cr-grid {
          max-width: 1140px; margin: 0 auto; padding: 0 24px 80px;
          display: grid; grid-template-columns: repeat(3, 1fr); gap: 22px;
        }
        @media (max-width: 900px) { .cr-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 600px) { .cr-grid { grid-template-columns: 1fr; } }

        .cr-card {
          background: #fff; border-radius: 12px; overflow: hidden;
          border: 1px solid rgba(41,75,90,.08);
          box-shadow: 0 6px 20px rgba(26,49,64,.05);
          transition: transform .25s, box-shadow .25s;
          display: flex; flex-direction: column;
        }
        .cr-card:hover { transform: translateY(-4px); box-shadow: 0 14px 32px rgba(26,49,64,.10); }

        .cr-card-photo {
          height: 170px; background: var(--mist);
          display: flex; align-items: center; justify-content: center; overflow: hidden;
        }
        .cr-card-photo img { width: 100%; height: 100%; object-fit: cover; }

        .cr-card-body { padding: 18px 20px 20px; display: flex; flex-direction: column; gap: 10px; flex: 1; }
        .cr-card-name { font-size: 17px; font-weight: 600; color: var(--ink); margin: 0; }
        .cr-card-model { font-size: 12px; color: var(--silver); margin: -6px 0 0; }

        .cr-specs { display: flex; gap: 14px; }
        .cr-spec { display: flex; align-items: center; gap: 5px; font-size: 12px; color: var(--teal-l); }

        .cr-price-row {
          margin-top: auto; padding-top: 14px; border-top: 1px dashed rgba(41,75,90,.14);
          display: flex; align-items: baseline; justify-content: space-between;
        }
        .cr-price { font-size: 20px; font-weight: 700; color: var(--gold-d); font-family: 'Playfair Display', serif; }
        .cr-price span { font-size: 11px; font-weight: 500; color: var(--silver); font-family: 'DM Sans', sans-serif; }
        .cr-rate-tag { font-size: 10.5px; text-transform: uppercase; letter-spacing: .5px; color: var(--silver); }

        .cr-book-btn {
          display: block; text-align: center; text-decoration: none;
          background: var(--teal); color: #fff; font-size: 13px; font-weight: 600;
          border-radius: 6px; padding: 11px; margin-top: 4px; transition: background .2s;
        }
        .cr-book-btn:hover { background: var(--teal-d); color: #fff; }

        .cr-state { max-width: 1140px; margin: 0 auto; padding: 60px 24px; text-align: center; color: var(--silver); }

        .cr-skeleton { background: linear-gradient(90deg, var(--mist) 25%, #f5f2ea 37%, var(--mist) 63%); background-size: 400% 100%; animation: cr-shimmer 1.4s infinite; }
        @keyframes cr-shimmer { 0% { background-position: 100% 50%; } 100% { background-position: 0 50%; } }

        .cr-pagination { display: flex; justify-content: center; gap: 6px; padding-bottom: 60px; }
        .cr-page-btn {
          width: 34px; height: 34px; border-radius: 8px; border: 1px solid rgba(41,75,90,.14);
          background: #fff; color: var(--ink); font-size: 13px; cursor: pointer;
        }
        .cr-page-btn.active { background: var(--teal); color: #fff; border-color: var(--teal); }
        .cr-page-btn:disabled { opacity: .4; cursor: not-allowed; }
      `}</style>

      <div className="cr-page">
        <Header />

        <section className="cr-hero">
          <div className="cr-eyebrow">{t("carRentalEyebrow")}</div>
          <h1 className="cr-title">
            {t("carRentalTitle")} <em>{t("carRentalTitleEm")}</em>
          </h1>
          <p className="cr-subtitle">
            {t("carRentalSubtitle")}
          </p>
        </section>

        <div className="cr-toolbar">
          <div className="cr-filters">
            {["all", "automatic", "manual"].map((filterType) => (
              <button
                key={filterType}
                className={`cr-pill-btn ${typeFilter === filterType ? "active" : ""}`}
                onClick={() => setTypeFilter(filterType)}
              >
                {filterType === "all" ? t("carFilterAll") : filterType === "automatic" ? t("carFilterAutomatic") : t("carFilterManual")}
              </button>
            ))}
          </div>

          <div className="cr-rate-toggle">
            {(["day", "month", "year"] as RateView[]).map((r) => (
              <button
                key={r}
                className={`cr-rate-btn ${rateView === r ? "active" : ""}`}
                onClick={() => setRateView(r)}
              >
                {r === "day" ? t("daily") : r === "month" ? t("monthly") : t("yearly")}
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <div className="cr-grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="cr-card">
                <div className="cr-card-photo cr-skeleton" />
                <div className="cr-card-body">
                  <div className="cr-skeleton" style={{ height: 16, width: "70%", borderRadius: 4 }} />
                  <div className="cr-skeleton" style={{ height: 12, width: "40%", borderRadius: 4 }} />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && error && (
          <div className="cr-state">{t("carError")}</div>
        )}

        {!loading && !error && cars.length === 0 && (
          <div className="cr-state">{t("carEmpty")}</div>
        )}

        {!loading && !error && cars.length > 0 && (
          <>
            <div className="cr-grid">
              {cars.map((car) => {
                const mainPhoto = car.photos?.find((p) => p.is_main) || car.photos?.[0];
                return (
                  <div className="cr-card" key={car.id}>
                    <div className="cr-card-photo">
                      {mainPhoto ? (
                        <img src={`${Back_url}/${mainPhoto.path}`} alt={car.name} />
                      ) : (
                        <CarPlaceholder />
                      )}
                    </div>
                    <div className="cr-card-body">
                      <h3 className="cr-card-name">{car.name}</h3>
                      <p className="cr-card-model">{car.model}</p>

                      <div className="cr-specs">
                        <span className="cr-spec"><SeatIcon /> {car.number} {t("seats")}</span>
                        <span className="cr-spec"><GearIcon /> {car.type}</span>
                      </div>

                      <div className="cr-price-row">
                        <div>
                          <span className="cr-rate-tag">{rateLabel} {t("rate")}</span><br />
                          <span className="cr-price">
                            {priceFor(car)} <span>OMR</span>
                          </span>
                        </div>
                      </div>

                      <Link to={`/car-rental/${car.id}`} className="cr-book-btn">
                        {t("viewAndBook")}
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>

            {lastPage > 1 && (
              <div className="cr-pagination">
                <button className="cr-page-btn" disabled={currentPage === 1} onClick={() => fetchCars(currentPage - 1)}>‹</button>
                {Array.from({ length: lastPage }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    className={`cr-page-btn ${p === currentPage ? "active" : ""}`}
                    onClick={() => fetchCars(p)}
                  >
                    {p}
                  </button>
                ))}
                <button className="cr-page-btn" disabled={currentPage === lastPage} onClick={() => fetchCars(currentPage + 1)}>›</button>
              </div>
            )}
          </>
        )}

        <DefaultFooter />
      </div>

      {showPromoModal && (
        <div className="cr-modal-overlay" onClick={closePromoModal}>
          <div className="cr-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cr-modal-banner">
              <button className="cr-modal-close" onClick={closePromoModal} aria-label="Close">✕</button>
              <div className="cr-modal-tag">{t("promoModalTag")}</div>
              <h3 className="cr-modal-heading">
                {t("promoModalTitle")} <em>{t("promoModalTitleEm")}</em>
              </h3>
            </div>
            <div className="cr-modal-body">
              <p className="cr-modal-text">
                {t("promoModalText")}
              </p>

              <div className="cr-modal-perks">
                <div className="cr-modal-perk">
                  <span className="cr-modal-perk-icon">✓</span>
                  {t("promoPerk1")}
                </div>
                <div className="cr-modal-perk">
                  <span className="cr-modal-perk-icon">✓</span>
                  {t("promoPerk2")}
                </div>
                <div className="cr-modal-perk">
                  <span className="cr-modal-perk-icon">✓</span>
                  {t("promoPerk3")}
                </div>
              </div>

              <div className="cr-modal-actions">
                <button className="cr-modal-cta" onClick={closePromoModal}>
                  {t("promoBrowseBtn")}
                </button>
                <button className="cr-modal-dismiss" onClick={closePromoModal}>
                  {t("promoLaterBtn")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </>
  );
};

export default CarRental;